import { useContext, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import CurrentUserContext from "../contexts/CurrentUserContext";
import AgreementContent from "../components/LicenseAgreement/AgreementContent";
import Button from "../components/Common/Button";
import { acceptLicenseAgreement } from "../lib/api";
import { customerNeedsLicenseAgreement } from "../utils/licenseAccess";
import {
  getLoginRedirectPath,
  resolvePostAuthRedirectPath,
} from "../utils/redirect";

export default function LicenseAgreement() {
  const { currentUser, updateCurrentUser } = useContext(CurrentUserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const needsAgreement = customerNeedsLicenseAgreement(currentUser);

  if (!needsAgreement) {
    return <Navigate to="/" replace />;
  }

  const resolveDestination = async () => {
    try {
      return await resolvePostAuthRedirectPath(currentUser, location.state?.from);
    } catch {
      return getLoginRedirectPath(location.state?.from);
    }
  };

  const handleAgree = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      await acceptLicenseAgreement();
    } catch (err) {
      setError(
        err?.message || "Something went wrong. Please try again."
      );
      setIsSubmitting(false);
      return;
    }

    updateCurrentUser({ hasAcceptedLicenseAgreement: true });
    navigate(await resolveDestination(), { replace: true });
  };

  return (
    <div
      className="flex min-h-[calc(100vh-160px)] items-center justify-center"
      data-testid="license-agreement-page"
    >
      <div className="flex w-full max-w-2xl flex-col gap-6 rounded-2xl border border-black/10 bg-white p-6 shadow-[0_20px_60px_rgba(16,24,40,0.08)] dark:border-white/10 dark:bg-darkThemeModalBg sm:p-8">
        <div className="max-h-[65vh] overflow-auto pr-1">
          <AgreementContent />
        </div>
        {error && (
          <p
            className="text-sm font-medium text-critical"
            role="alert"
            data-testid="license-agreement-error"
          >
            {error}
          </p>
        )}
        <div className="flex justify-end">
          <Button
            onClick={handleAgree}
            disabled={isSubmitting}
            data-testid="agree-button"
            className="flex h-10 min-w-24 items-center justify-center rounded-md bg-theme px-5 py-3 text-base font-semibold leading-5 tracking-[-0.32px] text-white transition-all duration-300 ease-in-out hover:bg-theme/80 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Submitting..." : "Agree"}
          </Button>
        </div>
      </div>
    </div>
  );
}
